import AddIcon from "@mui/icons-material/Add";
import { Autocomplete, Button, Checkbox, Chip, CircularProgress, TextField, Box } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * @function SelectField
 * @description A wrapper around MUI Autocomplete to provide a consistent API for selecting one or multiple options from a list.
 * @param {object} props Component props
 * @param {string} [props.label="Select"] The label for the select
 * @param {boolean} [props.disabled=false] Is the select disabled?
 * @param {boolean} [props.multiSelect=false] Is the select a multi-select?
 * @param {number} [props.limitTags=2] The maximum number of tags to display
 * @param {(filters?: {}) => Promise<import("axios").AxiosResponse<any, any>>} [props.getData] The function to call to get the options
 * @param {{[key: string]: string | number | boolean }} [props.getDataStaticFilters] The static filters to pass to getData
 * @param {(options: Object[]) => Object[]} [props.optionFilter] A function to filter the options before displaying them
 * @param {string} [props.searchField="name"] The field to search by
 * @param {Array|Object} [props.value] The value of the select - **array if multiSelect is true else single value**
 * @param {string} [props.placeholder] The placeholder for the select
 * @param {string[]} [props.optionValueFields=["name"]] The fields to use for the option label
 * @param {boolean} [props.required] Is the select required?
 * @param {boolean} [props.error] The error for the select
 * @param {string} [props.helperText] The helper text for the select
 * @param {() => void} [props.onChange] The function to call when the value changes
 * @param {boolean} [props.showAddButton=false] Whether to show the add button
 * @param {() => void} [props.onAddClick] The function to call when the add button is clicked
 * @param {string} [props.addButtonText="Add"] The text to display on the add button
 * @param {object} [props.style] The style for the select
 * @param {object} [props.defaultOption=null] An option object to always show at the top of the dropdown (e.g. `{ id: "default", name: "Company Project" }`)
 * @param {object} [props.listboxSx] Additional sx styles to merge into the listbox (style overrides only)
 * @param {boolean} [props.disableClearable=false] Hide the clear (x) button, for required fields that can never be empty
 * @returns {import("react").ReactElement} The component
 */
export const SelectField = ({
  label,
  disabled,
  multiSelect = false, // TODO: test it thoroughly
  limitTags = 2,
  getData,
  getDataStaticFilters,
  optionFilter,
  searchField = "name",
  value = multiSelect ? [] : null,
  placeholder = "",
  optionValueFields = ["name"],
  required = false,
  error = false,
  helperText = "",
  onChange = () => {},
  showAddButton = false,
  onAddClick = () => {},
  addButtonText = "Add",
  defaultOption = null,
  listboxSx = {},
  disableClearable = false,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]); // TODO: implement caching to improve performance (mainly for reopening)
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  const hasAutoFetchedNextPageRef = useRef(false);
  const [totalCount, setTotalCount] = useState(0);
  // Debounce scroll handler
  const scrollTimeout = useRef(null);
  const cacheRef = useRef({});

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimeout(scrollTimeout.current);
    };
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // reset pagination on new search
      setOptions([]);
      setHasMore(true);
      setTotalCount(0);
      hasAutoFetchedNextPageRef.current = false;
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  // Create a key representing the current filter + search state
  const cacheKey = useMemo(() => JSON.stringify({ search, filters: getDataStaticFilters }), [search, getDataStaticFilters]);

  // TODO: remove this side effect and handle data loading on respective events
  useEffect(() => {
    // fetch only when open and has more options and not loading
    if (!open || !hasMore || loading) return;

    const cachedData = cacheRef.current[cacheKey];
    if (cachedData && cachedData[page]) {
      // Use cached page data
      setOptions((prev) => {
        const mergedOptions = page === 1 ? cachedData[page] : [...prev, ...cachedData[page]];
        setHasMore(mergedOptions.length < cachedData.totalCount);
        return mergedOptions;
      });
      setTotalCount(cachedData.totalCount);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    getData({ [searchField]: search, page, ...getDataStaticFilters, limit: PAGE_SIZE })
      .then(({ data }) => {
        if (!active) return;

        // handle both array and ( rows, count ) response formats
        let rows, count;

        if (Array.isArray(data)) {
          rows = data;
          count = data.length;
        } else {
          rows = data?.rows || [];
          count = data?.count ?? rows.length;
        }

        // Cache page
        if (!cacheRef.current[cacheKey]) {
          cacheRef.current[cacheKey] = {};
        }
        cacheRef.current[cacheKey][page] = rows;
        cacheRef.current[cacheKey].totalCount = count;
        setTotalCount(count);

        setOptions((prev) => {
          const mergedOptions = page === 1 ? rows : [...prev, ...rows];
          setHasMore(rows.length === PAGE_SIZE); // if less than total count, then there are more options
          return mergedOptions;
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => {
      active = false;
    };
  }, [search, page, open, getData, searchField, getDataStaticFilters, cacheKey]);

  /**
   * Callback fired when the popup requests to be opened
   * @param {React.SyntheticEvent} event The event source of the callback.
   */
  const handleOpen = useCallback(() => {
    setOpen(true);
    setPage(1);
    setHasMore(true);
    setOptions([]);
    setTotalCount(0);
    hasAutoFetchedNextPageRef.current = false;
  }, []);

  /**
   * Callback fired when the popup requests to be closed.
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {"toggleInput" | "escape" | "selectOption" | "removeOption" | "blur"} reason The reason for the close event.
   */
  const handleClose = useCallback((event, reason) => {
    setOpen(false);
    setOptions([]);
    setSearchInput("");
    setTotalCount(0);
    hasAutoFetchedNextPageRef.current = false;
  }, []);

  /**
   * Returns the label for the given option object.
   * @param {Object} optionObject The option object.
   * @returns {string} The label for the option object.
   */
  const getOptionLabel = useCallback(
    (optionObject) => {
      if (!optionObject || optionObject?.isAddButton || optionObject?.isLoading || optionObject?.noResults) return "";
      return optionValueFields.reduce((label, field) => label + optionObject[field] + " ", "").trim();
    },
    [optionValueFields],
  );

  const handleSelectChange = useCallback(
    (event, newValue) => {
      onChange?.(newValue);
      setSearchInput("");
      if (!multiSelect) setOpen(false);
    },
    [multiSelect, onChange],
  );

  /**
   * Callback fired when the input value changes.
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {string} value The new value of the text input.
   * @param {"input" | "reset" | "clear" | "blur" | "selectOption" | "removeOption"} reason
   * @returns {void}
   */
  const handleSearchInputChange = useCallback((event, value, reason) => {
    if (reason === "input") setSearchInput(value); // TODO: handle reason=selectOption
    if (reason === "reset" || reason === "clear") setSearchInput("");
  }, []);

  const handleScroll = useCallback(
    (event) => {
      // If already scrolling, do nothing or Immediately return if no more data!
      if (scrollTimeout.current || !hasMore || loading) return;

      const listboxNode = event.currentTarget;
      const scrollBottom = listboxNode.scrollTop + listboxNode.clientHeight;
      const scrollHeight = listboxNode.scrollHeight;

      // Only fire if enough scrolled and not currently loading
      if (scrollBottom >= scrollHeight - 1 && !loading && hasMore) {
        scrollTimeout.current = setTimeout(() => {
          scrollTimeout.current = null;
          setPage((p) => p + 1);
        }, 100);
      }
    },
    [hasMore, loading],
  );

  const handleAddClick = () => {
    // Clear cache on add click event
    cacheRef.current = {};

    // Call original onAddClick prop
    onAddClick?.();
  };

  const filteredOptions = useMemo(() => {
    return typeof optionFilter === "function" ? optionFilter(options) : options;
  }, [optionFilter, options]);

  const displayedOptions = useMemo(() => {
    const defaultOptionLabel = defaultOption ? getOptionLabel(defaultOption).toLowerCase() : "";
    const isDefaultMatch = defaultOption && defaultOptionLabel.includes((searchInput || "").toLowerCase());
    const defaultOptionsArr = isDefaultMatch ? [{ ...defaultOption, isDefaultOption: true }] : [];

    const hasRealOptions = defaultOptionsArr.length > 0 || filteredOptions.length > 0;

    return [
      ...(showAddButton ? [{ isAddButton: true }] : []),
      ...defaultOptionsArr,
      ...filteredOptions,
      ...(loading && hasMore ? [{ isLoading: true }] : []),
      ...(!loading && !hasRealOptions ? [{ noResults: true }] : []),
    ];
  }, [showAddButton, defaultOption, filteredOptions, loading, hasMore, searchInput, getOptionLabel]);

  useEffect(() => {
    const shouldFetchNext =
      open &&
      hasMore &&
      !loading &&
      filteredOptions.length < PAGE_SIZE &&
      options.length < totalCount &&
      !hasAutoFetchedNextPageRef.current;

    if (shouldFetchNext) {
      hasAutoFetchedNextPageRef.current = true;
      setPage((currentPage) => currentPage + 1);
    }
  }, [filteredOptions.length, hasMore, loading, open, options.length, totalCount]);
  // renderOption is used to render each option in the list
  const renderOption = useCallback(
    (props, option, { selected }) => {
      // rendering add button
      if (option.isAddButton) {
        return (
          <li {...props} key="add-btn" style={{ padding: 0 }}>
            <Button
              fullWidth
              onClick={handleAddClick}
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              children={addButtonText}
              sx={{
                justifyContent: "center",
                p: "8px 16px",
                fontWeight: 500,
                color: "#674BF5",
                textTransform: "none",
                backgroundColor: "#F2ECF9",
                "&:hover": { backgroundColor: "#E8DAF8" },
              }}
            />
          </li>
        );
      }
      // rendering loader on search or no results found
      if (option.isLoading || option.noResults) {
        return (
          <li {...props} key="loading-text" style={{ padding: "8px 16px", fontStyle: "italic", opacity: 0.7 }}>
            {option.isLoading ? "Loading..." : "No results found"}
          </li>
        );
      }
      // rendering option
      const isDefault = option.isDefaultOption;
      return (
        <li
          {...props}
          key={option.id || (isDefault ? "default" : undefined)}
          style={{ ...props.style, backgroundColor: selected ? "#F2ECF9" : "inherit" }}
        >
          {multiSelect && (
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
              sx={{ "&.Mui-checked": { color: "#674BF5" } }}
            />
          )}
          {isDefault ? (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
              <Box sx={{ fontWeight: 600, color: "#674BF5" }}>{getOptionLabel(option)}</Box>
            </Box>
          ) : (
            getOptionLabel(option)
          )}
        </li>
      );
    },
    [onAddClick, addButtonText, getOptionLabel, multiSelect],
  );

  // renderInput is used to render the input field - handling search
  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        required={required}
        error={error}
        helperText={helperText}
        slotProps={{
          inputLabel: { sx: { "&.Mui-focused": { color: "#674BF5" } } },
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps?.endAdornment}
              </>
            ),
          },
          formHelperText: {
            sx: {
              minHeight: "20px", // reserve space to prevent UI jump
              margin: 0, // remove default margin
            },
          },
        }}
        sx={{
          mb: error && helperText ? 0 : 2,
          "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#674BF5" } },
        }}
      />
    ),
    [label, placeholder, required, error, helperText, loading],
  );

  const renderTags = useCallback(
    (value, getTagProps) => {
      const limit = limitTags;
      const numTags = value.length;

      return (
        <>
          {value.slice(0, limit).map((option, index) => (
            <Chip {...getTagProps({ index })} key={index} label={getOptionLabel(option)} size="small" />
          ))}
          {numTags > limit && `+${numTags - limit}`}
        </>
      );
    },
    [limitTags, getOptionLabel],
  );

  return (
    <Autocomplete
      label={label}
      options={displayedOptions}
      getOptionLabel={(option) => getOptionLabel(option)}
      value={value}
      inputValue={searchInput || (multiSelect ? "" : getOptionLabel(value))}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      size="small"
      disabled={disabled}
      loading={loading}
      disableClearable={disableClearable}
      multiple={multiSelect}
      limitTags={multiSelect ? limitTags : undefined}
      renderTags={multiSelect ? renderTags : undefined}
      disableCloseOnSelect={multiSelect}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      blurOnSelect={!multiSelect}
      filterOptions={(x) => x} // prevent client-side filtering
      onChange={handleSelectChange}
      onInputChange={handleSearchInputChange}
      clearOnBlur={false}
      renderOption={renderOption}
      renderInput={renderInput}
      slotProps={{ listbox: { onScroll: handleScroll, sx: { maxHeight: 40 * 9, overflowY: "auto", ...listboxSx } } }}
      sx={{ ...style }}
    />
  );
};
